import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { formatCurrency, OrderFee } from '@/utils/money';
import { LoyaltyEnrollSection } from '@/components/loyalty/loyalty-enroll-section';
import { LoyaltyPointsLine } from './loyalty-points-line';
import { OrderSummaryFees } from './order-summary-fees';
import { SalesRepOrderSummary } from './sales-rep-order-summary';
import { Text, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import {
  ValidateOrderFieldsFragment,
  ValidateOrderLoyaltyFieldsFragment,
} from '@/types';

// TODO: after revamp is 100%, calculate total price here
interface Props {
  calculatedTransactionFee: number;
  displayItemPrices: boolean;
  includeSubtotal: boolean;
  loading: boolean;
  loyalty: ValidateOrderLoyaltyFieldsFragment[];
  onLoyaltyEnrolled: () => void;
  order: ValidateOrderFieldsFragment | null;
  orderFees: OrderFee[];
  orderMinimumErrorCount: number;
  totalPrice: string | null;
  transactionFeeName?: string | null;
}

export const OrderSummaryFooter: React.FC<Props> = ({
  calculatedTransactionFee,
  displayItemPrices,
  includeSubtotal,
  loading,
  loyalty,
  onLoyaltyEnrolled,
  order,
  orderFees,
  orderMinimumErrorCount,
  totalPrice,
  transactionFeeName,
}) => {
  const [styles] = useStyles();

  const { currencyCode } = useVariantPackInfoContext();
  const salesRepMode = useSalesRepMode();
  const isOfflineModeActive = useOfflineModeActive();

  // ACCEPTED = points the restaurant is actively earning; everything else
  // (PENDING/DECLINED) is hypothetical "if enrolled" points.
  const enrolledLoyalty = loyalty.filter(
    (program) => program.enrollment_status === 'ACCEPTED',
  );
  const ifEnrolledLoyalty = loyalty.filter(
    (program) => program.enrollment_status !== 'ACCEPTED',
  );

  return (
    <View style={styles.base}>
      {calculatedTransactionFee > 0 ? (
        <View style={styles.row}>
          <Typography variant="paragraph-md">
            {i18n.t('estimated_fee', {
              transactionFeeName: transactionFeeName
                ? transactionFeeName
                : i18n.t('transaction_fee'),
            })}
          </Typography>
          <Text style={[TYPOGRAPHY.p]}>
            {formatCurrency(calculatedTransactionFee, currencyCode)}
          </Text>
        </View>
      ) : null}

      {order && displayItemPrices ? (
        <OrderSummaryFees
          currencyCode={currencyCode}
          includeSubtotal={includeSubtotal}
          isUpdating={loading}
          order={order}
          orderFees={orderFees}
        />
      ) : null}
      <View style={styles.totalRow}>
        <Text style={TYPOGRAPHY.h2}>{i18n.t('estimated_total')}</Text>
        <View style={styles.totalPrice}>
          <Text style={[TYPOGRAPHY.h2, FONTS.primary]}>{totalPrice}</Text>
        </View>
      </View>
      {orderMinimumErrorCount > 0 ? (
        <Text style={[TYPOGRAPHY.listSubtitle, styles.totalPriceWarning]}>
          {i18n.t('total_only_includes_fulfillable_items')}
        </Text>
      ) : null}
      <Text style={TYPOGRAPHY.listSubtitle}>
        {i18n.t('final_price_pending')}
      </Text>
      {enrolledLoyalty.length ? (
        <View style={styles.loyaltyContainer}>
          {enrolledLoyalty.map((loyaltyProgram) => (
            <LoyaltyPointsLine
              enrolled
              estimatedPoints={loyaltyProgram.estimated_points}
              key={loyaltyProgram.program_id}
              loading={loading}
              programName={loyaltyProgram.program_name}
            />
          ))}
        </View>
      ) : null}
      {ifEnrolledLoyalty.length ? (
        <View style={styles.loyaltyContainer}>
          {ifEnrolledLoyalty.map((loyaltyProgram) => (
            <LoyaltyPointsLine
              enrolled={false}
              estimatedPoints={loyaltyProgram.estimated_points}
              key={loyaltyProgram.program_id}
              loading={loading}
              programName={loyaltyProgram.program_name}
            >
              {/* can_enroll already accounts for the enrollment being PENDING. */}
              {loyaltyProgram.can_enroll ? (
                <LoyaltyEnrollSection
                  onLoyaltyEnrolled={onLoyaltyEnrolled}
                  program={loyaltyProgram}
                />
              ) : null}
            </LoyaltyPointsLine>
          ))}
        </View>
      ) : null}
      {order && salesRepMode && !isOfflineModeActive ? (
        <View style={styles.salesRepOrderSummaryContainer}>
          <SalesRepOrderSummary isUpdating={loading} order={order} />
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  loyaltyContainer: {
    gap: theme.sk.spacing.spaceSmall,
    marginVertical: theme.sk.spacing.spaceSmall,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.sk.spacing.spaceSmall,
  },
  salesRepOrderSummaryContainer: {
    marginTop: theme.sk.spacing.spaceSmall,
  },
  totalPrice: {
    alignItems: 'flex-end',
  },
  totalPriceWarning: {
    color: theme.colors.warningContent,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.sk.spacing.spaceXsmall,
  },
}));
